import { Link, Navigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { useMutation, useQuery } from "@tanstack/react-query";
import { register, self } from "../../http/auth";
import { toast } from "react-toastify";

const Register = () => {
  const { setUser, setToken, setRefreshToken, user } = useAuthStore();

  const { refetch } = useQuery({
    queryKey: ["self"],
    queryFn: self,
    enabled: false,
    retry: 0,
  });

  const mutation = useMutation({
    mutationFn: register,
    onSuccess: async (responseData) => {
      setToken(responseData?.data?.accessToken);
      setRefreshToken(responseData?.data?.refreshToken);
      const { data } = await refetch();
      setUser(data?.data?.user);
      toast.success("Account created successfully!");
    },
    onError: (error) => {
      console.error("Register error:", error);
      let message;
      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (!error?.response) {
        message = "Unable to connect to server. Please try again.";
      } else {
        message = "An unexpected error occurred. Please try again.";
      }
      toast.error(message, { autoClose: 5000 });
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    const firstName = form.firstName.value.trim();
    const lastName = form.lastName.value.trim();
    const username = form.username.value.trim();
    const email = form.email.value.trim();
    const password = form.password.value;
    const confirmPassword = form.confirmPassword.value;

    if (password !== confirmPassword) {
      toast.error("Passwords do not match", { autoClose: 5000 });
      return;
    }

    mutation.mutate({
      firstName,
      lastName,
      username,
      email,
      password,
    });
  };

  if (user) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex flex-col items-center justify-center p-6 min-h-[calc(100vh-128px)] bg-flyers-black">
      <form
        onSubmit={handleSubmit}
        className="bg-flyers-charcoal text-white p-8 rounded-lg border border-flyers-dark-gray shadow-orange-glow w-full max-w-md"
      >
        <h1 className="font-heading text-2xl font-black uppercase tracking-wide mb-6 text-center text-flyers-orange">
          Register
        </h1>

        {/* Grid for First and Last Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label mb-2">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              className="input"
              required
            />
          </div>
          <div>
            <label className="label mb-2">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              className="input"
              required
            />
          </div>
        </div>

        {/* Username */}
        <div className="mb-4">
          <label className="label mb-2">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            className="input"
            required
          />
        </div>

        {/* Email */}
        <div className="mb-4">
          <label className="label mb-2">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            className="input"
            required
          />
        </div>

        {/* Password & Confirm Password */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="label mb-2">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              className="input"
              required
            />
          </div>
          <div>
            <label className="label mb-2">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="input"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          className="bg-flyers-orange w-full hover:bg-flyers-orange-light text-white font-heading font-bold uppercase tracking-wider py-3 px-4 rounded transition-colors duration-150"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Registering..." : "Register"}
        </button>

        <p className="mt-6 text-center text-flyers-mid-gray">
          Already have an account?{" "}
          <Link to="/login" className="text-flyers-orange hover:text-flyers-orange-light transition-colors">
            Login
          </Link>
        </p>
      </form>
    </div>
  );
};

export default Register;
